import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";

/**
 * Advanced analytics API (feature key: advanced_analytics).
 *
 * The server exposes a semantic layer, not SQL: the client sends metric and
 * dimension KEYS taken from the catalogue endpoint. Nothing typed by a user
 * ever becomes part of a query.
 *
 * Every response carries a `freshness` block. Charts must render it — a figure
 * whose age is invisible is a figure the user over-trusts the moment the
 * refresh worker falls behind.
 */

export const AnalyticsKeys = {
  catalogue: (fleetId: number) => ["analytics-catalogue", fleetId] as const,
  query: (fleetId: number, query: unknown) => ["analytics-query", fleetId, query] as const,
  overview: (fleetId: number, from: string, to: string) =>
    ["analytics-overview", fleetId, from, to] as const,
  anomalies: (fleetId: number, from: string, to: string) =>
    ["analytics-anomalies", fleetId, from, to] as const,
  costs: (fleetId: number, filters: unknown) => ["costs", fleetId, filters] as const,
};

export interface MetricDefinition {
  key: string;
  label: string;
  unit: "cents" | "cents_per_km" | "km" | "kg" | "g_per_km" | "days" | "count" | "unit";
}

export interface AnalyticsCatalogue {
  metrics: MetricDefinition[];
  dimensions: Array<{ key: string; label: string }>;
  filters: string[];
  max_dimensions: number;
  max_rows: number;
  freshness: Freshness;
}

export interface Freshness {
  last_computed_at: string | null;
  pending_partitions: number;
  is_stale: boolean;
}

export interface AnalyticsQuery {
  metrics: string[];
  dimensions?: string[];
  filters?: Record<string, unknown>;
  compare_to?: "previous_period" | null;
  limit?: number;
}

export interface AnalyticsResult {
  metrics: string[];
  dimensions: string[];
  period: { from: string; to: string };
  rows: Array<Record<string, string | number | null>>;
  comparison: Array<Record<string, string | number | null>> | null;
  row_count: number;
  truncated: boolean;
  freshness: Freshness;
  units: Record<string, string>;
}

export const useAnalyticsCatalogue = (fleetId: number) => {
  const { data, ...res } = useQuery({
    queryKey: AnalyticsKeys.catalogue(fleetId),
    enabled: !!fleetId,
    staleTime: 5 * 60 * 1000, // the catalogue only changes on deploy
    queryFn: async (): Promise<AnalyticsCatalogue> => {
      const response = await axios.get(`/companies/${fleetId}/analytics/catalogue`);
      return response?.data;
    },
  });

  return { catalogue: data, ...res };
};

export const useAnalyticsQuery = (fleetId: number, query: AnalyticsQuery, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: AnalyticsKeys.query(fleetId, query),
    enabled: enabled && !!fleetId && query.metrics.length > 0,
    queryFn: async (): Promise<AnalyticsResult> => {
      const response = await axios.post(`/companies/${fleetId}/analytics/query`, query);
      return response?.data;
    },
  });

  return { result: data, ...res };
};

export const useAnalyticsOverview = (fleetId: number, from: string, to: string) => {
  const { data, ...res } = useQuery({
    queryKey: AnalyticsKeys.overview(fleetId, from, to),
    enabled: !!fleetId,
    queryFn: async () => {
      const response = await axios.get(`/companies/${fleetId}/analytics/overview`, {
        params: { from, to },
      });
      return response?.data;
    },
  });

  return { overview: data, ...res };
};

export interface Anomaly {
  type: string;
  metric: string;
  vehicle_id: number;
  score: number;
  value: number;
  peer_median?: number;
  deviation_pct?: number | null;
  peer_group?: { energy: string; category: string; size: number };
  explanation: string;
}

export const useAnomalies = (fleetId: number, from: string, to: string) => {
  const { data, ...res } = useQuery({
    queryKey: AnalyticsKeys.anomalies(fleetId, from, to),
    enabled: !!fleetId,
    queryFn: async (): Promise<{ findings: Anomaly[]; method: string }> => {
      const response = await axios.get(`/companies/${fleetId}/analytics/anomalies`, {
        params: { from, to },
      });
      return response?.data;
    },
  });

  return { anomalies: data?.findings ?? [], method: data?.method, ...res };
};

// ── Cost ledger ──────────────────────────────────────────────────────────────

export interface CostEntry {
  id: number;
  vehicle_id: number;
  category: string;
  occurred_on: string;
  amount_ht_cents: number;
  vat_cents: number;
  vat_recoverable_cents: number;
  quantity: number | null;
  unit: string | null;
  odometer_km: number | null;
  label: string | null;
  source: string;
}

export const COST_CATEGORIES = [
  "FUEL", "ENERGY", "MAINTENANCE", "TYRES", "INSURANCE", "LEASE",
  "DEPRECIATION", "TAX", "TOLL", "FINE", "CLAIM", "ACCESSORY", "OTHER",
];

export const useCosts = (
  fleetId: number,
  filters: { vehicle_id?: number; category?: string; from?: string; to?: string } = {}
) => {
  const { data, ...res } = useQuery({
    queryKey: AnalyticsKeys.costs(fleetId, filters),
    enabled: !!fleetId,
    queryFn: async () => {
      const response = await axios.get(`/companies/${fleetId}/costs`, { params: filters });
      return response?.data;
    },
  });

  return { costsPayload: data, ...res };
};

/**
 * Amounts are sent in currency units; the server converts to cents once.
 * Never send cents from here — two conversion sites is one too many.
 */
export const useCreateCost = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      vehicle_id: number;
      category: string;
      occurred_on: string;
      amount_ht: number;
      vat?: number;
      vat_recoverable?: number;
      quantity?: number;
      unit?: string;
      odometer_km?: number;
      label?: string;
    }) => {
      const response = await axios.post(`/companies/${fleetId}/costs`, payload);
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costs", fleetId] });
      // The fact table refreshes asynchronously; drop the analytics caches so
      // the next read waits for fresh numbers instead of showing stale ones.
      queryClient.invalidateQueries({ queryKey: ["analytics-overview", fleetId] });
      queryClient.invalidateQueries({ queryKey: ["analytics-query", fleetId] });
    },
  });
};

export const useDeleteCost = (fleetId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (costId: number) => {
      const response = await axios.delete(`/companies/${fleetId}/costs/${costId}`);
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costs", fleetId] });
      queryClient.invalidateQueries({ queryKey: ["analytics-overview", fleetId] });
    },
  });
};

/**
 * Formats a value according to the unit the SERVER declared for that metric.
 * Guessing from the metric name is how a cost in cents ends up displayed as
 * kilometres.
 */
export const formatMetric = (
  value: number | string | null,
  unit: string | undefined,
  locale = "fr-FR"
): string => {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(numeric)) return String(value);

  switch (unit) {
    case "cents":
      return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(numeric / 100);
    case "cents_per_km":
      return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numeric / 100)} €/km`;
    case "km":
      return `${new Intl.NumberFormat(locale).format(Math.round(numeric))} km`;
    case "kg":
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(numeric)} kg`;
    case "g_per_km":
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(numeric)} g/km`;
    case "days":
      return `${new Intl.NumberFormat(locale).format(numeric)} j`;
    default:
      return new Intl.NumberFormat(locale).format(numeric);
  }
};
